import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    computed,
    inject,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
    UntypedFormControl,
    UntypedFormGroup,
    ReactiveFormsModule,
} from "@angular/forms";
import { Subject, Subscription, timer } from "rxjs";
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    switchMap,
    takeUntil,
} from "rxjs/operators";

import { LayoutFacade } from "@hermes/aphrodite-layout-state";
import { SvgIconComponent } from "@hermes/aphrodite-svg";
import { LOCALE, WINDOW } from "@hermes/core-app-core";
import { AnalyticsService } from "@hermes/hermes-web-fragments-analytics";
import { MenuFacade, FlatCategories } from "@hermes/domains-shell-states-menu-state";
import {
    FeatureFlagFacade,
    HEADER_FULL_TRANSPARENT,
} from "@hermes/hermes-web-states-flipper-state";

import { stopEventPropagation } from "@hermes/utils-generic-helpers";

import { SearchEvent } from "../../events/search.event";
import { isPrintableSearch } from "../../helpers/header-search.helper";
import { Suggestion } from "../../model/suggestions";
import { SuggestionItemPipe } from "../../pipes/suggestion-item.pipe";
import { HeaderSearchService } from "../../services/header-search.service";

import { HeaderSearchSuggestionsComponent } from "../header-search-suggestions/header-search-suggestions.component";
import { activeMenu } from "../menu-bar/menu-bar.state";

import {
    SEARCH_DEBOUNCE_TIME_IN_MS,
    SEARCH_MINIMUM_LENGTH,
} from "./header-search-bar.constants";

@Component({
    selector: "h-header-search-bar",
    imports: [
        ReactiveFormsModule,
        SvgIconComponent,
        HeaderSearchSuggestionsComponent,
        SuggestionItemPipe,
    ],
    templateUrl: "./header-search-bar.component.html",
    styleUrls: ["./header-search-bar.component.scss"],
})
export class HeaderSearchBarComponent implements OnInit, OnDestroy {
    private headerSearchService = inject(HeaderSearchService);
    private locale = inject(LOCALE);
    private cd = inject(ChangeDetectorRef);
    private window = inject<Window>(WINDOW);
    private analyticsService = inject(AnalyticsService);
    private menuFacade = inject(MenuFacade);
    private featureFlagFacade = inject(FeatureFlagFacade);
    private layoutFacade = inject(LayoutFacade);

    @Output()
    public searchExpanded: EventEmitter<boolean> = new EventEmitter<boolean>();
    @ViewChild("searchInput", { static: true })
    public searchInput!: ElementRef<HTMLInputElement>;

    public formAction!: string;
    public isFocused: boolean = false;
    public suggestions: undefined | Suggestion[] = undefined;
    public selected: number | undefined;
    public searchTerm: string = "";
    public searchForm = new UntypedFormGroup({
        search: new UntypedFormControl({ value: "", disabled: true }),
    });

    public isHeaderTransparent = toSignal(
        this.layoutFacade.headerTransparent$,
        { initialValue: false },
    );

    private categories: FlatCategories = {};
    private scrollListener!: EventListener;
    private subscription = new Subscription();
    private destroy$ = new Subject<void>();

    public isHeaderFullTransparentFF = toSignal(
        this.featureFlagFacade.isActivated(HEADER_FULL_TRANSPARENT),
        { initialValue: false },
    );

    public isAnyParentCategoryActive = computed(() => {
        const active = activeMenu();
        return !!active && active.isActive();
    });

    public shouldShowWhiteText = computed(
        () =>
            this.isHeaderTransparent() &&
            this.isHeaderFullTransparentFF() &&
            !this.isAnyParentCategoryActive(),
    );

    // Fix for Safari browser mobile:
    // On Safari Browser mobile, when we click on the search bar the keyboard display generates a scroll and
    // causes a blink of the search bar content, so we need to init a flag to ignore these scroll
    private ignoreNextScroll = false;

    public ngOnInit(): void {
        this.scrollListener = () => {
            if (this.ignoreNextScroll) {
                this.ignoreNextScroll = false;
                return;
            }

            this.searchInput.nativeElement.blur();
        };
        this.formAction = `${this.locale.urlPrefix}/search/`;

        this.fetchCategories();
        this.initAutocomplete();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    public onSubmit(): void {
        const formValue = this.searchForm.value;
        if (formValue.search && isPrintableSearch(formValue.search)) {
            this.window.location.assign(
                `${this.locale.urlPrefix}/search/?s=${encodeURIComponent(formValue.search)}`,
            );
        }
    }

    public handleSearch(): void {
        if (this.isFocused) {
            this.onSubmit();
        } else {
            this.searchInput.nativeElement.focus();
        }
    }

    private computeSearchTerm(value: unknown): string {
        if (typeof value === "string") {
            const sanitizedValue = value.trim();

            if (sanitizedValue.length >= SEARCH_MINIMUM_LENGTH) {
                return sanitizedValue;
            }
        }

        return "";
    }

    public handleFocus(): void {
        this.isFocused = true;
        this.searchExpanded.emit(true);

        // Ignore the first scroll generates by the keyboard display
        this.ignoreNextScroll = true;

        const SCROLL_DELAY = 500; // ms
        // Delay handler to prevent instant removal of suggestions and subcomponent.
        this.subscription.add(
            timer(SCROLL_DELAY).subscribe(() => {
                this.window.addEventListener("scroll", this.scrollListener);
            }),
        );

        const searchTerm = this.computeSearchTerm(
            this.searchForm.get("search")?.value,
        );

        this.subscription.add(
            this.headerSearchService
                .getSuggestions(searchTerm, this.categories)
                .pipe(filter(() => this.isFocused))
                .subscribe((suggestions) => {
                    this.saveSuggestions(suggestions);
                }),
        );
    }

    public handleBlur(): void {
        const BLUR_DELAY = 100; // ms
        // Delay handler to prevent instant removal of suggestions and subcomponent.
        this.subscription.add(
            timer(BLUR_DELAY).subscribe(() => {
                this.isFocused = false;
                this.searchExpanded.emit(false);
                this.window.removeEventListener("scroll", this.scrollListener);
                this.suggestions = undefined;
                this.selected = undefined;
            }),
        );
    }

    public handleEsc(): void {
        this.suggestions = undefined;
        this.selected = undefined;
    }

    public handleDown(event: Event): void {
        stopEventPropagation(event);
        if (!this.hasSuggestionSelected(this.selected)) {
            return this.selectFirstSuggestion();
        }

        if (this.isLastSuggestionSelected()) {
            return this.selectFirstSuggestion();
        }

        this.selected++;
    }

    public handleUp(event: Event): void {
        stopEventPropagation(event);
        if (!this.hasSuggestionSelected(this.selected)) {
            return this.selectLastSuggestion();
        }

        if (this.isFirstSuggestionSelected()) {
            return this.selectLastSuggestion();
        }

        this.selected--;
    }

    public handleEnter(event: Event): void {
        if (this.selected !== undefined && this.selected >= 0) {
            // Prevent form submit
            stopEventPropagation(event);
            const suggestionLabel = this.suggestions?.[this.selected].label;
            this.searchForm.get("search")?.setValue(suggestionLabel, {
                emitEvent: false,
            });
            this.sendSearchEvent(
                suggestionLabel ?? "undefined",
                this.selected + 1,
                !!this.suggestions?.[this.selected]?.sku,
            );
            const url = this.suggestions?.[this.selected]?.url;
            if (typeof url === "string") {
                this.window.location.assign(url);
            }
            this.suggestions = undefined;
            this.selected = undefined;
        }
        // Else form submit
    }

    public resetSearchField(): void {
        this.searchForm.reset({ search: "" }, { emitEvent: false });
    }

    private sendSearchEvent(
        suggestionLabel: string,
        position: number,
        isProductPage: boolean,
    ): void {
        this.analyticsService.sendData(
            new SearchEvent({
                eventaction: `Search suggest list : product ${isProductPage ? "page" : "grid"}`,
                eventlabel: `${this.searchTerm} : ${suggestionLabel}`,
                eventvalue: position,
            }),
        );
    }

    private fetchCategories(): void {
        this.menuFacade.fetchMenu();

        this.subscription.add(
            this.menuFacade.categories$
                .pipe(filter((categories) => !!categories))
                .subscribe((categories) => {
                    this.searchForm.get("search")?.enable({ emitEvent: false });
                    this.categories = categories as FlatCategories;
                }),
        );
    }

    private initAutocomplete() {
        this.searchForm
            .get("search")
            ?.valueChanges.pipe(
                debounceTime(SEARCH_DEBOUNCE_TIME_IN_MS),
                map((value) => this.computeSearchTerm(value)),
                distinctUntilChanged(),
                filter((searchTerm) => searchTerm !== this.searchTerm),
                switchMap((searchTerm) =>
                    this.headerSearchService.getSuggestions(
                        searchTerm,
                        this.categories,
                    ),
                ),
                filter(() => this.isFocused), // Only accept results when bar is focused
                takeUntil(this.destroy$), // unsubscribe when component is destroyed
            )
            .subscribe((suggestions) => this.saveSuggestions(suggestions));
    }

    private saveSuggestions(suggestions: Suggestion[]) {
        this.searchTerm = this.computeSearchTerm(
            this.searchForm.get("search")?.value,
        );
        this.suggestions = suggestions;
        delete this.selected;
        // Handle OnPush pages
        this.cd.markForCheck();
    }

    private hasSuggestionSelected(
        selected: number | undefined,
    ): selected is number {
        return selected !== undefined && selected >= 0;
    }

    private isFirstSuggestionSelected(): boolean {
        return this.selected === 0;
    }

    private isLastSuggestionSelected(): boolean {
        return (
            this.suggestions !== undefined &&
            this.selected === this.suggestions.length - 1
        );
    }

    private selectFirstSuggestion() {
        this.selected = 0;
    }

    private selectLastSuggestion() {
        if (this.suggestions) {
            this.selected = this.suggestions.length - 1;
        }
    }
}
